/**
 * Widget page – for Add to Home Screen on Android/iOS.
 * Shows: latest scores (live match), last match played, next match scheduled for the user.
 * Optimized for standalone/fullscreen when opened from home screen icon.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGame } from '../contexts/GameContext';
import { usePlayerCache } from '../contexts/PlayerCacheContext';
import * as readApi from '../api/readApi';
import { toUtcMs } from '../utils/dateTimeUtils';
import { formatScheduledDateTimeInTimezone, DEFAULT_TIMEZONE } from '../utils/dateTimeUtils';
import { sortMatchesChronologically } from '../utils/dateFormat';
import LoadingBlock from '../components/LoadingBlock';
import './Widget.css';

function formatScoreSummary(scores) {
  if (!scores) return '—';
  const games = [];
  if (scores.game1) games.push(`${scores.game1.player1}-${scores.game1.player2}`);
  if (scores.game2) games.push(`${scores.game2.player1}-${scores.game2.player2}`);
  if (scores.game3) games.push(`${scores.game3.player1}-${scores.game3.player2}`);
  return games.length ? games.join(', ') : '—';
}

function getMatchSideLabel(match, getPlayerName) {
  if (match.matchType === 'doubles') {
    return {
      side1: match.team1Name || 'Team 1',
      side2: match.team2Name || 'Team 2',
    };
  }
  return {
    side1: getPlayerName(match.player1Id) || 'Player 1',
    side2: getPlayerName(match.player2Id) || 'Player 2',
  };
}

export default function Widget() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { gameId } = useGame();
  const userTimezone = userProfile?.timezone || DEFAULT_TIMEZONE;
  const { getPlayers, getPlayerName } = usePlayerCache();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const gameIdStr = gameId || 'table-tennis';
  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const data = await readApi.getScheduleData(gameIdStr);
        if (cancelled) return;
        const list = (data.matches || []).map((m) => ({ ...m }));
        list.sort(sortMatchesChronologically);
        setMatches(list);
        const playerIds = new Set();
        list.forEach((m) => {
          if (m.player1Id) playerIds.add(m.player1Id);
          if (m.player2Id) playerIds.add(m.player2Id);
          if (m.refereeId) playerIds.add(m.refereeId);
          (m.team1Players || []).forEach((id) => playerIds.add(id));
          (m.team2Players || []).forEach((id) => playerIds.add(id));
        });
        if (playerIds.size) getPlayers([...playerIds]).catch(() => {});
      } catch (e) {
        if (!cancelled) setMatches([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [currentUser?.uid, gameIdStr, getPlayers]);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      if (detail.gameId !== gameIdStr || !currentUser?.uid) return;
      readApi.getScheduleData(gameIdStr).then((data) => {
        const list = (data.matches || []).map((m) => ({ ...m }));
        list.sort(sortMatchesChronologically);
        setMatches(list);
        const playerIds = new Set();
        list.forEach((m) => {
          if (m.player1Id) playerIds.add(m.player1Id);
          if (m.player2Id) playerIds.add(m.player2Id);
          if (m.refereeId) playerIds.add(m.refereeId);
          (m.team1Players || []).forEach((id) => playerIds.add(id));
          (m.team2Players || []).forEach((id) => playerIds.add(id));
        });
        if (playerIds.size) getPlayers([...playerIds]).catch(() => {});
      }).catch(() => {});
    };
    window.addEventListener('scheduleCacheUpdated', handler);
    window.addEventListener('scheduleCachePatched', handler);
    return () => {
      window.removeEventListener('scheduleCacheUpdated', handler);
      window.removeEventListener('scheduleCachePatched', handler);
    };
  }, [currentUser?.uid, gameIdStr, getPlayers]);

  const isMyMatch = useMemo(() => {
    if (!currentUser?.uid) return () => false;
    return (match) => {
      if (match.matchType === 'doubles') {
        const inTeam1 = match.team1Players && match.team1Players.includes(currentUser.uid);
        const inTeam2 = match.team2Players && match.team2Players.includes(currentUser.uid);
        return inTeam1 || inTeam2 || match.refereeId === currentUser.uid;
      }
      return match.player1Id === currentUser.uid ||
             match.player2Id === currentUser.uid ||
             match.refereeId === currentUser.uid;
    };
  }, [currentUser?.uid]);

  const { latestScoreMatch, lastMatchPlayed, nextMatchScheduled } = useMemo(() => {
    const myMatches = matches.filter(isMyMatch);
    let latestScoreMatch = null;
    let lastMatchPlayed = null;
    let nextMatchScheduled = null;

    const scheduled = myMatches.filter((m) => m.status === 'scheduled');
    const completed = myMatches.filter((m) => m.status === 'completed');
    const inProgress = myMatches.filter((m) => m.status === 'in-progress');

    if (inProgress.length > 0) {
      latestScoreMatch = inProgress[0];
    }
    if (completed.length > 0) {
      completed.sort((a, b) => {
        const aMs = toUtcMs(a.updatedAt) ?? toUtcMs(a.scheduledAt) ?? toUtcMs(a.scheduledDate) ?? 0;
        const bMs = toUtcMs(b.updatedAt) ?? toUtcMs(b.scheduledAt) ?? toUtcMs(b.scheduledDate) ?? 0;
        return bMs - aMs;
      });
      lastMatchPlayed = completed[0];
    }
    if (scheduled.length > 0) {
      scheduled.sort(sortMatchesChronologically);
      nextMatchScheduled = scheduled[0];
    }

    return { latestScoreMatch, lastMatchPlayed, nextMatchScheduled };
  }, [matches, isMyMatch]);

  if (loading) {
    return (
      <div className="widget-page">
        <div className="widget-header">
          <h1 className="widget-app-name">GradedPlay</h1>
          <p className="widget-subtitle">Scores &amp; schedule</p>
        </div>
        <LoadingBlock message="Loading…" fullPage={false} className="widget-loading" />
      </div>
    );
  }

  const openMatch = (matchId) => {
    navigate(`/match/${matchId}`);
  };
  const openSchedule = () => {
    navigate('/schedule');
  };
  const openApp = () => {
    navigate('/');
  };

  return (
    <div className="widget-page">
      <div className="widget-header">
        <h1 className="widget-app-name">GradedPlay</h1>
        <p className="widget-subtitle">Scores &amp; schedule</p>
      </div>

      <div className="widget-cards">
        {latestScoreMatch && (
          <section className="widget-card widget-card-live" aria-label="Latest score">
            <h2 className="widget-card-title">Live score</h2>
            {(() => {
              const { side1, side2 } = getMatchSideLabel(latestScoreMatch, getPlayerName);
              const scoreStr = formatScoreSummary(latestScoreMatch.scores);
              return (
                <>
                  <p className="widget-match-up">{side1} vs {side2}</p>
                  <p className="widget-score">{scoreStr}</p>
                  <button
                    type="button"
                    className="widget-card-action"
                    onClick={() => openMatch(latestScoreMatch.id)}
                  >
                    View match
                  </button>
                </>
              );
            })()}
          </section>
        )}

        {lastMatchPlayed && (
          <section className="widget-card widget-card-last" aria-label="Last match played">
            <h2 className="widget-card-title">Last match</h2>
            {(() => {
              const { side1, side2 } = getMatchSideLabel(lastMatchPlayed, getPlayerName);
              const scoreStr = formatScoreSummary(lastMatchPlayed.scores);
              const winnerId = lastMatchPlayed.scores?.matchWinner === 'player1'
                ? (lastMatchPlayed.player1Id || (lastMatchPlayed.team1Players && lastMatchPlayed.team1Players[0]))
                : (lastMatchPlayed.player2Id || (lastMatchPlayed.team2Players && lastMatchPlayed.team2Players[0]));
              const winnerName = lastMatchPlayed.matchType === 'doubles'
                ? (lastMatchPlayed.scores?.matchWinner === 'player1' ? side1 : side2)
                : getPlayerName(winnerId);
              return (
                <>
                  <p className="widget-match-up">{side1} vs {side2}</p>
                  <p className="widget-score">{scoreStr}</p>
                  {winnerName && <p className="widget-winner">Winner: {winnerName}</p>}
                  <button
                    type="button"
                    className="widget-card-action"
                    onClick={() => openMatch(lastMatchPlayed.id)}
                  >
                    View match
                  </button>
                </>
              );
            })()}
          </section>
        )}

        {nextMatchScheduled && (
          <section className="widget-card widget-card-next" aria-label="Next match scheduled">
            <h2 className="widget-card-title">Next match</h2>
            {(() => {
              const { side1, side2 } = getMatchSideLabel(nextMatchScheduled, getPlayerName);
              const when = formatScheduledDateTimeInTimezone(
                nextMatchScheduled.scheduledAt ?? nextMatchScheduled.scheduledDate,
                nextMatchScheduled.scheduledTime,
                userTimezone,
                { useSeparator: ' at ' }
              );
              return (
                <>
                  <p className="widget-match-up">{side1} vs {side2}</p>
                  <p className="widget-when">{when}</p>
                  <button
                    type="button"
                    className="widget-card-action"
                    onClick={() => openMatch(nextMatchScheduled.id)}
                  >
                    Open match
                  </button>
                </>
              );
            })()}
          </section>
        )}

        {!latestScoreMatch && !lastMatchPlayed && !nextMatchScheduled && (
          <div className="widget-empty">
            <p>No live score, last match, or next match for you right now.</p>
            <button type="button" className="widget-btn-primary" onClick={openSchedule}>
              View schedule
            </button>
          </div>
        )}
      </div>

      <footer className="widget-footer">
        <button type="button" className="widget-link" onClick={openSchedule}>Schedule</button>
        <span className="widget-footer-sep">·</span>
        <button type="button" className="widget-link" onClick={openApp}>Open app</button>
      </footer>
    </div>
  );
}
